import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  CreditCard, Plus, Pencil, Trash2, X, Check,
  Star, ToggleLeft, ToggleRight, Infinity, Loader2,
  ChevronDown, ChevronRight, Tag, Percent, Copy, BarChart3
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PLATFORM_FEATURES, FEATURE_CATEGORIES, planHasFeature } from "@/lib/platform-features";

interface Plan {
  id: string; name: string; slug: string; description: string | null;
  price_monthly: number; price_yearly: number | null; billing_type: string;
  max_orders_per_month: number | null; max_products: number | null;
  max_categories: number | null; features: any; trial_days: number | null;
  is_active: boolean; is_featured: boolean; sort_order: number;
  promo_price_monthly?: number | null;
  promo_price_yearly?: number | null;
  promo_label?: string | null;
  promo_ends_at?: string | null;
}

interface PlatformCoupon {
  id: string;
  code: string;
  description: string | null;
  type: string;
  value: number;
  max_uses: number | null;
  uses_count: number;
  valid_from: string;
  valid_until: string | null;
  applicable_plan_ids: string[];
  min_plan_price: number;
  is_active: boolean;
  created_at: string;
  billing_cycle: string;
  free_days: number | null;
  for_new_users_only: boolean;
}

interface Props {
  plans: Plan[];
  setPlans: React.Dispatch<React.SetStateAction<Plan[]>>;
  logAction: (action: string, entityType: string, entityId: string, details?: any) => void;
}

const AdminPlansTab = ({ plans, setPlans, logAction }: Props) => {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Plan | null>(null);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState("plans");

  // Plan form state
  const [formName, setFormName] = useState("");
  const [formSlug, setFormSlug] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formPriceMonthly, setFormPriceMonthly] = useState(0);
  const [formPriceYearly, setFormPriceYearly] = useState(0);
  const [formMaxOrders, setFormMaxOrders] = useState<number | null>(null);
  const [formMaxProducts, setFormMaxProducts] = useState<number | null>(null);
  const [formMaxCategories, setFormMaxCategories] = useState<number | null>(null);
  const [formIsActive, setFormIsActive] = useState(true);
  const [formIsFeatured, setFormIsFeatured] = useState(false);
  const [formSortOrder, setFormSortOrder] = useState(0);
  const [formTrialDays, setFormTrialDays] = useState<number | null>(null);
  const [formFeatures, setFormFeatures] = useState<Record<string, boolean>>({});
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  // Promo fields
  const [formPromoMonthly, setFormPromoMonthly] = useState<number | null>(null);
  const [formPromoYearly, setFormPromoYearly] = useState<number | null>(null);
  const [formPromoLabel, setFormPromoLabel] = useState("");
  const [formPromoEndsAt, setFormPromoEndsAt] = useState("");

  // Coupon state
  const [coupons, setCoupons] = useState<PlatformCoupon[]>([]);
  const [couponDialogOpen, setCouponDialogOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<PlatformCoupon | null>(null);
  const [savingCoupon, setSavingCoupon] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [couponDescription, setCouponDescription] = useState("");
  const [couponType, setCouponType] = useState("percentage");
  const [couponValue, setCouponValue] = useState(0);
  const [couponMaxUses, setCouponMaxUses] = useState<number | null>(null);
  const [couponValidUntil, setCouponValidUntil] = useState("");
  const [couponPlanIds, setCouponPlanIds] = useState<string[]>([]);
  const [couponIsActive, setCouponIsActive] = useState(true);
  const [couponBillingCycle, setCouponBillingCycle] = useState("both");
  const [couponFreeDays, setCouponFreeDays] = useState<number | null>(null);
  const [couponForNewOnly, setCouponForNewOnly] = useState(false);

  const formatCurrency = (v: number) => `R$ ${v.toFixed(2).replace(".", ",")}`;
  const generateSlug = (name: string) =>
    name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

  // Fetch coupons
  const fetchCoupons = useCallback(async () => {
    const { data } = await supabase.from("platform_coupons").select("*").order("created_at", { ascending: false });
    setCoupons((data || []) as any as PlatformCoupon[]);
  }, []);

  useEffect(() => { fetchCoupons(); }, [fetchCoupons]);

  const parseFeaturesFromPlan = (features: any): Record<string, boolean> => {
    if (!features) return {};
    if (typeof features === "object" && !Array.isArray(features)) return features;
    if (Array.isArray(features)) {
      const obj: Record<string, boolean> = {};
      features.forEach((f: string) => {
        const match = PLATFORM_FEATURES.find(pf => pf.key === f || pf.label === f);
        if (match) obj[match.key] = true;
      });
      return obj;
    }
    return {};
  };

  const openCreate = () => {
    setEditing(null);
    setFormName(""); setFormSlug(""); setFormDescription("");
    setFormPriceMonthly(0); setFormPriceYearly(0);
    setFormMaxOrders(null); setFormMaxProducts(null); setFormMaxCategories(null);
    setFormIsActive(true); setFormIsFeatured(false); setFormSortOrder(plans.length);
    setFormTrialDays(null); setFormFeatures({}); setExpandedCategories([]);
    setFormPromoMonthly(null); setFormPromoYearly(null); setFormPromoLabel(""); setFormPromoEndsAt("");
    setDialogOpen(true);
  };

  const openEdit = (plan: Plan) => {
    setEditing(plan);
    setFormName(plan.name); setFormSlug(plan.slug); setFormDescription(plan.description || "");
    setFormPriceMonthly(plan.price_monthly); setFormPriceYearly(plan.price_yearly || 0);
    setFormMaxOrders(plan.max_orders_per_month); setFormMaxProducts(plan.max_products); setFormMaxCategories(plan.max_categories);
    setFormIsActive(plan.is_active); setFormIsFeatured(plan.is_featured); setFormSortOrder(plan.sort_order);
    setFormTrialDays(plan.trial_days); setFormFeatures(parseFeaturesFromPlan(plan.features)); setExpandedCategories([]);
    setFormPromoMonthly(plan.promo_price_monthly ?? null);
    setFormPromoYearly(plan.promo_price_yearly ?? null);
    setFormPromoLabel(plan.promo_label || "");
    setFormPromoEndsAt(plan.promo_ends_at ? plan.promo_ends_at.slice(0, 16) : "");
    setDialogOpen(true);
  };

  const toggleFeature = (key: string) => {
    setFormFeatures(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleCategory = (cat: string) => {
    setExpandedCategories(prev => prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]);
  };

  const selectAllInCategory = (cat: string, checked: boolean) => {
    const keys = PLATFORM_FEATURES.filter(f => f.category === cat).map(f => f.key);
    setFormFeatures(prev => {
      const next = { ...prev };
      keys.forEach(k => { next[k] = checked; });
      return next;
    });
  };

  const enabledCount = Object.values(formFeatures).filter(Boolean).length;

  const save = async () => {
    if (!formName) { toast({ title: "Nome obrigatório", variant: "destructive" }); return; }
    setSaving(true);
    const slug = formSlug || generateSlug(formName);
    const payload: any = {
      name: formName, slug, description: formDescription || null,
      price_monthly: formPriceMonthly, price_yearly: formPriceYearly || null,
      billing_type: "monthly",
      max_orders_per_month: formMaxOrders, max_products: formMaxProducts, max_categories: formMaxCategories,
      trial_days: formTrialDays,
      features: formFeatures as any,
      is_active: formIsActive, is_featured: formIsFeatured, sort_order: formSortOrder,
      promo_price_monthly: formPromoMonthly || null,
      promo_price_yearly: formPromoYearly || null,
      promo_label: formPromoLabel || null,
      promo_ends_at: formPromoEndsAt ? new Date(formPromoEndsAt).toISOString() : null,
    };

    if (editing) {
      const { data, error } = await supabase.from("plans").update(payload).eq("id", editing.id).select().single();
      if (error) { toast({ title: "Erro", description: error.message, variant: "destructive" }); }
      else {
        setPlans(prev => prev.map(p => p.id === editing.id ? { ...data } as Plan : p));
        logAction("plan_updated", "plan", editing.id, { name: formName });
        toast({ title: "Plano atualizado!" });
        setDialogOpen(false);
      }
    } else {
      const { data, error } = await supabase.from("plans").insert(payload).select().single();
      if (error) { toast({ title: "Erro", description: error.message, variant: "destructive" }); }
      else {
        setPlans(prev => [...prev, { ...data } as Plan]);
        logAction("plan_created", "plan", data.id, { name: formName });
        toast({ title: "Plano criado!" });
        setDialogOpen(false);
      }
    }
    setSaving(false);
  };

  const deletePlan = async (plan: Plan) => {
    if (!confirm(`Excluir "${plan.name}"?`)) return;
    const { error } = await supabase.from("plans").delete().eq("id", plan.id);
    if (error) { toast({ title: "Erro", description: error.message, variant: "destructive" }); }
    else { setPlans(prev => prev.filter(p => p.id !== plan.id)); logAction("plan_deleted", "plan", plan.id); toast({ title: "Plano excluído" }); }
  };

  const toggleActive = async (plan: Plan) => {
    const { error } = await supabase.from("plans").update({ is_active: !plan.is_active }).eq("id", plan.id);
    if (!error) setPlans(prev => prev.map(p => p.id === plan.id ? { ...p, is_active: !p.is_active } : p));
  };

  const countEnabledFeatures = (features: any): number => {
    if (!features || Array.isArray(features)) return 0;
    return Object.values(features).filter(Boolean).length;
  };

  // Coupon helpers
  const openCreateCoupon = () => {
    setEditingCoupon(null);
    setCouponCode(""); setCouponDescription(""); setCouponType("percentage");
    setCouponValue(0); setCouponMaxUses(null); setCouponValidUntil("");
    setCouponPlanIds([]); setCouponIsActive(true);
    setCouponBillingCycle("both"); setCouponFreeDays(null); setCouponForNewOnly(false);
    setCouponDialogOpen(true);
  };

  const openEditCoupon = (c: PlatformCoupon) => {
    setEditingCoupon(c);
    setCouponCode(c.code); setCouponDescription(c.description || ""); setCouponType(c.type);
    setCouponValue(c.value); setCouponMaxUses(c.max_uses); setCouponValidUntil(c.valid_until ? c.valid_until.slice(0, 16) : "");
    setCouponPlanIds(c.applicable_plan_ids || []); setCouponIsActive(c.is_active);
    setCouponBillingCycle(c.billing_cycle || "both"); setCouponFreeDays(c.free_days); setCouponForNewOnly(c.for_new_users_only);
    setCouponDialogOpen(true);
  };

  const saveCoupon = async () => {
    if (!couponCode.trim()) { toast({ title: "Código obrigatório", variant: "destructive" }); return; }
    setSavingCoupon(true);
    const { data: { user } } = await supabase.auth.getUser();
    const payload: any = {
      code: couponCode.trim().toUpperCase(),
      description: couponDescription || null,
      type: couponType,
      value: couponValue,
      max_uses: couponMaxUses,
      valid_until: couponValidUntil ? new Date(couponValidUntil).toISOString() : null,
      applicable_plan_ids: couponPlanIds.length > 0 ? couponPlanIds : [],
      is_active: couponIsActive,
      billing_cycle: couponBillingCycle,
      free_days: couponFreeDays,
      for_new_users_only: couponForNewOnly,
      created_by: user!.id,
    };

    if (editingCoupon) {
      const { error } = await supabase.from("platform_coupons").update(payload).eq("id", editingCoupon.id);
      if (error) toast({ title: "Erro", description: error.message, variant: "destructive" });
      else { toast({ title: "Cupom atualizado!" }); setCouponDialogOpen(false); fetchCoupons(); }
    } else {
      const { error } = await supabase.from("platform_coupons").insert(payload);
      if (error) toast({ title: "Erro", description: error.message, variant: "destructive" });
      else {
        logAction("coupon_created", "platform_coupon", "", { code: couponCode });
        toast({ title: "Cupom criado!" }); setCouponDialogOpen(false); fetchCoupons();
      }
    }
    setSavingCoupon(false);
  };

  const deleteCoupon = async (c: PlatformCoupon) => {
    if (!confirm(`Excluir cupom "${c.code}"?`)) return;
    await supabase.from("platform_coupons").delete().eq("id", c.id);
    logAction("coupon_deleted", "platform_coupon", c.id, { code: c.code });
    fetchCoupons();
  };

  const toggleCouponActive = async (c: PlatformCoupon) => {
    await supabase.from("platform_coupons").update({ is_active: !c.is_active }).eq("id", c.id);
    fetchCoupons();
  };

  const hasActivePromo = (plan: Plan) => {
    if (!plan.promo_price_monthly && !plan.promo_price_yearly) return false;
    if (plan.promo_ends_at && new Date(plan.promo_ends_at) < new Date()) return false;
    return true;
  };

  return (
    <div className="space-y-4">
      <Tabs value={activeSection} onValueChange={setActiveSection}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-display font-semibold text-lg">Planos & Cupons</h2>
            <p className="text-xs text-muted-foreground">{plans.length} planos · {coupons.length} cupons · {PLATFORM_FEATURES.length} recursos</p>
          </div>
          <TabsList>
            <TabsTrigger value="plans" className="gap-1.5 text-xs"><CreditCard className="h-3.5 w-3.5" />Planos</TabsTrigger>
            <TabsTrigger value="coupons" className="gap-1.5 text-xs"><Tag className="h-3.5 w-3.5" />Cupons</TabsTrigger>
          </TabsList>
        </div>

        {/* ===== PLANS TAB ===== */}
        <TabsContent value="plans" className="space-y-4 mt-4">
          <div className="flex justify-end">
            <Button onClick={openCreate} size="sm" className="bg-gradient-primary text-primary-foreground">
              <Plus className="h-4 w-4 mr-2" />Novo Plano
            </Button>
          </div>

          {plans.length === 0 ? (
            <div className="rounded-xl border border-border bg-card p-12 text-center text-muted-foreground">
              <CreditCard className="h-8 w-8 mx-auto mb-2 opacity-30" /><p>Nenhum plano</p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {plans.map((plan) => (
                <motion.div key={plan.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  className={`relative rounded-xl border bg-card p-5 flex flex-col ${plan.is_featured ? "border-primary ring-1 ring-primary/20" : "border-border"} ${!plan.is_active ? "opacity-50" : ""}`}>
                  {plan.is_featured && (
                    <div className="absolute -top-2.5 left-1/2 -translate-x-1/2">
                      <Badge className="bg-primary text-primary-foreground text-[10px]"><Star className="h-3 w-3 mr-1" />Popular</Badge>
                    </div>
                  )}
                  {hasActivePromo(plan) && (
                    <div className="absolute -top-2.5 right-3">
                      <Badge className="bg-emerald-500 text-white text-[10px]"><Percent className="h-3 w-3 mr-1" />{(plan as any).promo_label || "Promoção"}</Badge>
                    </div>
                  )}
                  <div className="flex items-start justify-between mb-3">
                    <div><h3 className="font-display font-bold text-base">{plan.name}</h3><p className="text-[10px] text-muted-foreground">{plan.description}</p></div>
                    <Badge variant={plan.is_active ? "default" : "secondary"} className="text-[10px] shrink-0">{plan.is_active ? "Ativo" : "Inativo"}</Badge>
                  </div>
                  <div className="mb-4">
                    {hasActivePromo(plan) ? (
                      <>
                        <span className="text-sm line-through text-muted-foreground">{formatCurrency(plan.price_monthly)}</span>
                        <span className="text-2xl font-bold font-display ml-2 text-emerald-500">{formatCurrency((plan as any).promo_price_monthly || plan.price_monthly)}</span>
                        <span className="text-xs text-muted-foreground">/mês</span>
                      </>
                    ) : (
                      <>
                        <span className="text-2xl font-bold font-display">{formatCurrency(plan.price_monthly)}</span>
                        <span className="text-xs text-muted-foreground">/mês</span>
                      </>
                    )}
                    {plan.price_yearly && plan.price_yearly > 0 && <p className="text-[10px] text-muted-foreground">ou {formatCurrency(plan.price_yearly)}/ano</p>}
                  </div>
                  <div className="space-y-2 mb-4 flex-1">
                    {[{ l: "Pedidos/mês", v: plan.max_orders_per_month }, { l: "Produtos", v: plan.max_products }, { l: "Categorias", v: plan.max_categories }].map(({ l, v }) => (
                      <div key={l} className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">{l}</span>
                        <span className="font-medium">{v ? v.toLocaleString("pt-BR") : <span className="flex items-center gap-1"><Infinity className="h-3 w-3" />Ilimitado</span>}</span>
                      </div>
                    ))}
                    {plan.trial_days != null && plan.trial_days > 0 && (
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">Trial</span>
                        <span className="font-medium">{plan.trial_days} dias</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between text-xs pt-1 border-t border-border/50">
                      <span className="text-muted-foreground">Recursos</span>
                      <span className="font-medium">{countEnabledFeatures(plan.features)}/{PLATFORM_FEATURES.length}</span>
                    </div>
                  </div>
                  {countEnabledFeatures(plan.features) > 0 && (
                    <div className="space-y-1 mb-4 border-t border-border pt-3 max-h-32 overflow-y-auto">
                      {PLATFORM_FEATURES.filter(f => planHasFeature(plan.features, f.key)).map(f => (
                        <div key={f.key} className="flex items-start gap-2 text-xs text-muted-foreground">
                          <Check className="h-3 w-3 text-emerald-500 mt-0.5 shrink-0" />{f.label}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-1 pt-2 border-t border-border">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(plan)} className="flex-1 text-xs h-8"><Pencil className="h-3 w-3 mr-1" />Editar</Button>
                    <Button variant="ghost" size="sm" onClick={() => toggleActive(plan)} className="text-xs h-8">{plan.is_active ? <ToggleRight className="h-4 w-4 text-emerald-500" /> : <ToggleLeft className="h-4 w-4" />}</Button>
                    <Button variant="ghost" size="sm" onClick={() => deletePlan(plan)} className="text-xs h-8 text-destructive hover:text-destructive"><Trash2 className="h-3 w-3" /></Button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ===== COUPONS TAB ===== */}
        <TabsContent value="coupons" className="space-y-4 mt-4">
          <div className="flex justify-end">
            <Button onClick={openCreateCoupon} size="sm" className="bg-gradient-primary text-primary-foreground gap-2">
              <Plus className="h-4 w-4" />Novo Cupom
            </Button>
          </div>

          {coupons.length === 0 ? (
            <div className="rounded-xl border border-border bg-card p-12 text-center text-muted-foreground">
              <Tag className="h-8 w-8 mx-auto mb-2 opacity-30" /><p>Nenhum cupom criado</p>
            </div>
          ) : (
            <div className="space-y-3">
              {coupons.map(c => (
                <Card key={c.id} className={`p-4 ${!c.is_active ? "opacity-50" : ""}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                      <Tag className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono font-bold text-sm">{c.code}</span>
                        <Badge variant="outline" className="text-[10px]">
                          {c.type === "percentage" ? `${c.value}%` : formatCurrency(c.value)}
                        </Badge>
                        {c.max_uses && (
                          <Badge variant="secondary" className="text-[10px]">{c.uses_count}/{c.max_uses} usos</Badge>
                        )}
                        {c.valid_until && (
                          <Badge variant="secondary" className="text-[10px]">
                            até {new Date(c.valid_until).toLocaleDateString("pt-BR")}
                          </Badge>
                        )}
                        {c.applicable_plan_ids?.length > 0 && (
                          <Badge variant="secondary" className="text-[10px]">
                            {c.applicable_plan_ids.length} plano(s)
                          </Badge>
                        )}
                        {c.billing_cycle && c.billing_cycle !== "both" && (
                          <Badge variant="secondary" className="text-[10px]">
                            {c.billing_cycle === "monthly" ? "Mensal" : "Anual"}
                          </Badge>
                        )}
                        {c.free_days && (
                          <Badge variant="secondary" className="text-[10px]">
                            {c.free_days} dias grátis
                          </Badge>
                        )}
                        {c.for_new_users_only && (
                          <Badge variant="outline" className="text-[10px] border-primary/30 text-primary">
                            Novos usuários
                          </Badge>
                        )}
                      </div>
                      {c.description && <p className="text-xs text-muted-foreground mt-1">{c.description}</p>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => {
                        navigator.clipboard.writeText(c.code);
                        toast({ title: "Código copiado!" });
                      }}>
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditCoupon(c)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Switch checked={c.is_active} onCheckedChange={() => toggleCouponActive(c)} />
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => deleteCoupon(c)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Plan Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Editar Plano" : "Novo Plano"}</DialogTitle></DialogHeader>
          <div className="space-y-5">
            {/* Basic info */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Nome</Label>
                <Input value={formName} onChange={(e) => { setFormName(e.target.value); if (!editing) setFormSlug(generateSlug(e.target.value)); }} placeholder="Ex: Profissional" className="h-9 text-sm" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Slug</Label>
                <Input value={formSlug} onChange={(e) => setFormSlug(e.target.value)} className="h-9 text-sm" /></div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Descrição</Label>
              <Input value={formDescription} onChange={(e) => setFormDescription(e.target.value)} className="h-9 text-sm" /></div>

            {/* Pricing */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Preço Mensal (R$)</Label>
                <Input type="number" step="0.01" min="0" value={formPriceMonthly} onChange={(e) => setFormPriceMonthly(parseFloat(e.target.value) || 0)} className="h-9 text-sm" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Preço Anual (R$)</Label>
                <Input type="number" step="0.01" min="0" value={formPriceYearly || ""} onChange={(e) => setFormPriceYearly(parseFloat(e.target.value) || 0)} className="h-9 text-sm" /></div>
            </div>

            {/* Promo Pricing */}
            <div className="rounded-lg border border-dashed border-emerald-500/30 bg-emerald-500/5 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Percent className="h-4 w-4 text-emerald-500" />
                <Label className="text-xs font-semibold text-emerald-600">Preço Promocional (opcional)</Label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-xs">Promo Mensal (R$)</Label>
                  <Input type="number" step="0.01" min="0" value={formPromoMonthly ?? ""} onChange={(e) => setFormPromoMonthly(e.target.value ? parseFloat(e.target.value) : null)} placeholder="Sem promoção" className="h-9 text-sm" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Promo Anual (R$)</Label>
                  <Input type="number" step="0.01" min="0" value={formPromoYearly ?? ""} onChange={(e) => setFormPromoYearly(e.target.value ? parseFloat(e.target.value) : null)} placeholder="Sem promoção" className="h-9 text-sm" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-xs">Label da Promoção</Label>
                  <Input value={formPromoLabel} onChange={(e) => setFormPromoLabel(e.target.value)} placeholder="Ex: Black Friday" className="h-9 text-sm" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Válido até</Label>
                  <Input type="datetime-local" value={formPromoEndsAt} onChange={(e) => setFormPromoEndsAt(e.target.value)} className="h-9 text-sm" /></div>
              </div>
            </div>

            {/* Trial */}
            <div className="space-y-1.5">
              <Label className="text-xs">Dias de Teste (Trial)</Label>
              <Input type="number" min="0" value={formTrialDays ?? ""} onChange={(e) => setFormTrialDays(e.target.value ? parseInt(e.target.value) : null)} placeholder="Sem trial" className="h-9 text-sm" />
            </div>

            {/* Limits */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { l: "Pedidos/mês", v: formMaxOrders, set: setFormMaxOrders },
                { l: "Produtos", v: formMaxProducts, set: setFormMaxProducts },
                { l: "Categorias", v: formMaxCategories, set: setFormMaxCategories },
              ].map(({ l, v, set }) => (
                <div key={l} className="space-y-1.5"><Label className="text-xs">{l}</Label>
                  <Input type="number" min="0" value={v ?? ""} onChange={(e) => set(e.target.value ? parseInt(e.target.value) : null)} placeholder="Ilimitado" className="h-9 text-sm" /></div>
              ))}
            </div>

            {/* Feature toggles */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">Recursos da Plataforma</Label>
                <Badge variant="outline" className="text-[10px]">{enabledCount}/{PLATFORM_FEATURES.length} ativos</Badge>
              </div>
              <div className="rounded-lg border border-border divide-y divide-border">
                {FEATURE_CATEGORIES.map(cat => {
                  const catFeatures = PLATFORM_FEATURES.filter(f => f.category === cat);
                  const catEnabled = catFeatures.filter(f => formFeatures[f.key]).length;
                  const isExpanded = expandedCategories.includes(cat);
                  const allEnabled = catEnabled === catFeatures.length;
                  return (
                    <div key={cat}>
                      <button onClick={() => toggleCategory(cat)} className="w-full flex items-center justify-between p-3 hover:bg-secondary/50 transition-colors">
                        <div className="flex items-center gap-3">
                          {isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                          <span className="text-sm font-medium">{cat}</span>
                          <Badge variant="secondary" className="text-[10px]">{catEnabled}/{catFeatures.length}</Badge>
                        </div>
                        <div onClick={e => e.stopPropagation()}>
                          <Checkbox checked={allEnabled} onCheckedChange={(checked) => selectAllInCategory(cat, !!checked)} />
                        </div>
                      </button>
                      {isExpanded && (
                        <div className="px-3 pb-3 space-y-1">
                          {catFeatures.map(feature => (
                            <label key={feature.key} className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${formFeatures[feature.key] ? "bg-primary/5" : "hover:bg-secondary/50"}`}>
                              <Checkbox checked={!!formFeatures[feature.key]} onCheckedChange={() => toggleFeature(feature.key)} />
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium">{feature.label}</p>
                                <p className="text-[10px] text-muted-foreground">{feature.description}</p>
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Switches */}
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2"><Switch checked={formIsActive} onCheckedChange={setFormIsActive} /><Label className="text-xs">Ativo</Label></div>
              <div className="flex items-center gap-2"><Switch checked={formIsFeatured} onCheckedChange={setFormIsFeatured} /><Label className="text-xs">Destaque</Label></div>
            </div>

            <Button onClick={save} disabled={saving} className="w-full bg-gradient-primary text-primary-foreground">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
              {editing ? "Salvar Alterações" : "Criar Plano"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Coupon Dialog */}
      <Dialog open={couponDialogOpen} onOpenChange={setCouponDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Tag className="h-5 w-5 text-primary" />{editingCoupon ? "Editar Cupom" : "Novo Cupom"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Código *</Label>
              <Input value={couponCode} onChange={(e) => setCouponCode(e.target.value.toUpperCase())} placeholder="EX: BLACKFRIDAY50" className="h-9 text-sm font-mono" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Descrição</Label>
              <Input value={couponDescription} onChange={(e) => setCouponDescription(e.target.value)} placeholder="Descrição interna" className="h-9 text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Tipo</Label>
                <Select value={couponType} onValueChange={setCouponType}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Porcentagem (%)</SelectItem>
                    <SelectItem value="fixed">Valor Fixo (R$)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Valor</Label>
                <Input type="number" step="0.01" min="0" value={couponValue} onChange={(e) => setCouponValue(parseFloat(e.target.value) || 0)} className="h-9 text-sm" />
              </div>
            </div>

            {/* Free days for 100% coupons */}
            {couponType === "percentage" && couponValue === 100 && (
              <div className="space-y-1.5 rounded-lg border border-dashed border-primary/30 bg-primary/5 p-3">
                <Label className="text-xs font-semibold text-primary">Dias de acesso gratuito</Label>
                <p className="text-[10px] text-muted-foreground">Para cupons de 100%, defina quantos dias o usuário terá acesso grátis.</p>
                <Input type="number" min="1" value={couponFreeDays ?? ""} onChange={(e) => setCouponFreeDays(e.target.value ? parseInt(e.target.value) : null)} placeholder="Ex: 30, 60, 90" className="h-9 text-sm" />
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Máximo de usos</Label>
                <Input type="number" min="1" value={couponMaxUses ?? ""} onChange={(e) => setCouponMaxUses(e.target.value ? parseInt(e.target.value) : null)} placeholder="Ilimitado" className="h-9 text-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Válido até</Label>
                <Input type="datetime-local" value={couponValidUntil} onChange={(e) => setCouponValidUntil(e.target.value)} className="h-9 text-sm" />
              </div>
            </div>

            {/* Billing cycle */}
            <div className="space-y-1.5">
              <Label className="text-xs">Ciclo de faturamento</Label>
              <Select value={couponBillingCycle} onValueChange={setCouponBillingCycle}>
                <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="both">Mensal e Anual</SelectItem>
                  <SelectItem value="monthly">Apenas Mensal</SelectItem>
                  <SelectItem value="yearly">Apenas Anual</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Applicable plans */}
            <div className="space-y-2">
              <Label className="text-xs">Planos aplicáveis</Label>
              <p className="text-[10px] text-muted-foreground">Deixe vazio para aplicar a todos os planos.</p>
              <div className="border rounded-lg p-2 max-h-36 overflow-y-auto space-y-1">
                {plans.map(p => (
                  <label key={p.id} className={`flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer text-xs transition-colors ${couponPlanIds.includes(p.id) ? "bg-primary/10 text-primary" : "hover:bg-secondary"}`}>
                    <Checkbox checked={couponPlanIds.includes(p.id)} onCheckedChange={() => {
                      setCouponPlanIds(prev => prev.includes(p.id) ? prev.filter(id => id !== p.id) : [...prev, p.id]);
                    }} />
                    <span>{p.name} ({formatCurrency(p.price_monthly)}/mês)</span>
                  </label>
                ))}
              </div>
            </div>

            {/* New users only */}
            <div className="flex items-center gap-2">
              <Switch checked={couponForNewOnly} onCheckedChange={setCouponForNewOnly} />
              <Label className="text-xs">Apenas para novos usuários (cadastro)</Label>
            </div>

            <div className="flex items-center gap-2">
              <Switch checked={couponIsActive} onCheckedChange={setCouponIsActive} />
              <Label className="text-xs">Ativo</Label>
            </div>

            <Button onClick={saveCoupon} disabled={savingCoupon} className="w-full bg-gradient-primary text-primary-foreground">
              {savingCoupon ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
              {editingCoupon ? "Salvar" : "Criar Cupom"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPlansTab;
